import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { ThemeProvider } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { buildTheme } from './index';

// Three states, not two. "System" is the default and the one most people want: a yard office that
// switches its machines to dark at dusk expects this to follow. Light and dark are explicit choices
// that override it, which is why the preference is stored as a choice rather than as a boolean.
const KEY = 'appearance';
const MODES = ['system', 'light', 'dark'];

const Context = createContext({ appearance: 'system', setAppearance: () => {}, mode: 'light' });

// Storage is unavailable in a private window and in some managed browsers, and a preference is
// never worth failing a page load over.
const remembered = () => {
  try {
    const held = window.localStorage.getItem(KEY);
    return MODES.includes(held) ? held : 'system';
  } catch {
    return 'system';
  }
};

export const ThemeModeProvider = ({ children }) => {
  const [appearance, setStored] = useState(remembered);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const mode = appearance === 'system' ? (prefersDark ? 'dark' : 'light') : appearance;

  const setAppearance = (next) => {
    setStored(MODES.includes(next) ? next : 'system');
    try { window.localStorage.setItem(KEY, next); } catch { /* a preference is not worth an error */ }
  };

  // The browser chrome around the page — the address bar on a phone, the title bar of an installed
  // app — takes its colour from here, so a dark page in a white frame is avoided.
  useEffect(() => {
    const tag = document.querySelector('meta[name="theme-color"]');
    if (tag) tag.setAttribute('content', mode === 'dark' ? '#0E1620' : '#123247');
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const theme = useMemo(() => buildTheme(mode), [mode]);
  const value = useMemo(() => ({ appearance, setAppearance, mode }), [appearance, mode]);

  return (
    <Context.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </Context.Provider>
  );
};

export const useAppearance = () => useContext(Context);

export default ThemeModeProvider;
